/**
 * Top-bar banner surfaced after auth resolves whenever there are
 * unfinished upload batches in IndexedDB. The banner is the user's
 * entry point to either resume (re-acquire file handles via the picker
 * flow) or discard (cancel every incomplete batch).
 *
 * We intentionally do NOT auto-resume. Surprise file-system prompts on
 * page load are jarring; the user explicitly clicks Resume so the
 * picker dialog is expected.
 *
 * The banner subscribes to firebase.auth and to the upload queue's
 * incomplete-batch list. Refreshes when the list changes (after resume
 * finishes flipping rows, after discard cancels them).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { CloudOff, RefreshCw, X, Folder } from 'lucide-react';
import { auth } from '../firebase';
import type { UploadBatch } from '../services/upload/uploadQueue';
import { toast } from 'sonner';

export const ResumeUploadsBanner: React.FC = () => {
  const [user, setUser] = useState<{ uid: string } | null>(() => auth.currentUser ? { uid: auth.currentUser.uid } : null);
  const [batches, setBatches] = useState<UploadBatch[]>([]);
  const [busy, setBusy] = useState(false);
  // Total file count across batches — drives the headline copy.
  const totalFiles = batches.reduce((acc, b) => acc + b.totalFiles, 0);

  // Track auth state — only query the queue once a user is signed in.
  useEffect(() => {
    return auth.onAuthStateChanged(u => setUser(u ? { uid: u.uid } : null));
  }, []);

  const refresh = useCallback(async () => {
    if (!user) { setBatches([]); return; }
    try {
      const { getUploadQueue } = await import('../services/upload/uploadQueue');
      const queue = getUploadQueue();
      const list = await queue.listIncompleteBatches();
      setBatches(list);
    } catch (e) {
      console.warn('[ResumeUploadsBanner] failed to list incomplete batches:', (e as Error)?.message);
    }
  }, [user]);

  useEffect(() => { void refresh(); }, [refresh]);

  const handleResume = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { getUploadQueue } = await import('../services/upload/uploadQueue');
      const { resumeBatch } = await import('../services/upload/fileHandleResolver');
      const queue = getUploadQueue();
      let totalResolved = 0;
      let totalUnresolved = 0;
      for (const batch of batches) {
        const summary = await resumeBatch(queue, batch.id);
        totalResolved += summary.resolved;
        totalUnresolved += summary.unresolved;
      }
      if (totalResolved === 0 && totalUnresolved === 0) {
        toast.message('No files to resume — all rows already terminal.');
      } else if (totalUnresolved === 0) {
        toast.success(`${totalResolved} file(s) re-bound. Upload queue will pick them up.`);
      } else {
        toast.warning(`Resumed ${totalResolved}, marked ${totalUnresolved} as not-found. Re-upload those manually if needed.`);
      }
      await refresh();
    } catch (e) {
      toast.error(`Resume failed: ${(e as Error)?.message ?? 'unknown error'}`);
    } finally {
      setBusy(false);
    }
  }, [batches, busy, refresh]);

  const handleDiscard = useCallback(async () => {
    if (busy) return;
    // PH-C2 — replace window.confirm with the canonical openConfirmDialog
    // primitive (portal-backed AlertDialog) for a11y + iframe-safety.
    // Semantics preserved: prompt copy + gating identical.
    const { openConfirmDialog } = await import('./ui/confirmDialog');
    const ok = await openConfirmDialog({
      title: 'Discard unfinished uploads?',
      body: `Discard ${batches.length} unfinished upload batch(es)? Files won't be uploaded.`,
      confirmLabel: 'Discard',
      cancelLabel: 'Keep',
      tone: 'danger',
    });
    if (!ok) return;
    setBusy(true);
    try {
      const { getUploadQueue } = await import('../services/upload/uploadQueue');
      const queue = getUploadQueue();
      for (const batch of batches) {
        await queue.cancelBatch(batch.id);
      }
      toast.success(`Discarded ${batches.length} batch(es).`);
      await refresh();
    } catch (e) {
      toast.error(`Discard failed: ${(e as Error)?.message ?? 'unknown error'}`);
    } finally {
      setBusy(false);
    }
  }, [batches, busy, refresh]);

  if (!user || batches.length === 0) return null;

  return (
    <div
      className="w-full bg-warning/15 border-b border-warning/40 text-text-primary"
      role="status"
      data-testid="resume-uploads-banner"
    >
      <div className="px-4 py-2 flex items-center gap-3">
        <CloudOff className="w-4 h-4 text-warning shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-bold uppercase tracking-widest">
            You have {totalFiles} unfinished upload{totalFiles === 1 ? '' : 's'}
            {batches.length > 1 ? ` across ${batches.length} batches` : ''}
          </div>
          <div className="text-[10px] text-text-muted truncate">
            Click Resume to re-select your files — browsers can't keep file handles across reloads.
          </div>
        </div>
        <button
          onClick={() => window.dispatchEvent(new CustomEvent('open-upload-queue-panel'))}
          className="text-[10px] font-bold uppercase tracking-widest text-text-secondary hover:text-text-primary px-3 py-1.5 border border-border hover:bg-state-hover inline-flex items-center gap-1.5"
          data-testid="resume-uploads-view-queue"
        >
          <Folder className="w-3 h-3" /> View queue
        </button>
        <button
          onClick={handleResume}
          disabled={busy}
          className="text-[10px] font-bold uppercase tracking-widest text-text-primary px-3 py-1.5 rounded-md border border-warning/60 hover:bg-warning/20 disabled:opacity-50 inline-flex items-center gap-1.5"
          data-testid="resume-uploads-resume"
        >
          <RefreshCw className={`w-3 h-3 ${busy ? 'animate-spin' : ''}`} /> Resume
        </button>
        <button
          onClick={handleDiscard}
          disabled={busy}
          className="text-[10px] font-bold uppercase tracking-widest text-text-secondary hover:text-danger px-3 py-1.5 border border-border hover:border-danger/40 disabled:opacity-50 inline-flex items-center gap-1.5"
          data-testid="resume-uploads-discard"
        >
          <X className="w-3 h-3" /> Discard
        </button>
      </div>
    </div>
  );
};
